import { Button } from '@base-ui/react/button';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import styled from 'styled-components';
import api, { queryClient } from '@/api';
import { apiErrorDetail } from '@/api/errorDetail';
import type { components } from '@/api/qraft/schema';
import { Breadcrumbs } from '~/components/ui/Breadcrumbs';
import { ItemBand, ItemVersionMenu } from '~/components/ui/ContentNav';
import { DetailPage } from '~/components/ui/Editor';
import { useEditMode, validateEditSearch } from '~/components/ui/editSearch';
import { Field, FieldLabel, FieldValue } from '~/components/ui/FieldDisplay';
import { PageTitle } from '~/components/ui/Page';
import { finalQuizTitle, QuizBadge } from '~/components/ui/QuizBadge';
import { LockedQuizNotice } from '~/components/ui/QuizLock';
import { RichContent } from '~/components/ui/RichContent';
import { FieldGrid, Section, SectionTitle } from '~/components/ui/Section';
import { deletePrompt } from '~/components/ui/Versions';
import buttonStyles from '~/styles/button.module.css';
import {
  acceptedSpellings,
  clozeHtml,
  QUESTION_TYPE_LABELS,
  QuestionEditor,
} from './QuestionEditor';

// The admin read carries the answer key; the student `Question` shape doesn't.
type QuestionAdmin = components['schemas']['QuestionAdmin'];
type Answer = NonNullable<QuestionAdmin['answers']>[number];

export const Route = createFileRoute('/_admin/questions/$questionId')({
  component: QuestionDetail,
  validateSearch: validateEditSearch,
});

function QuestionDetail() {
  const { questionId } = Route.useParams();
  const questionIdNum = Number(questionId);
  const { edit } = Route.useSearch();
  const { editing, startEditing, stopEditing } = useEditMode(edit === true);

  const { data, isLoading, error } = api.adminGetQuestion.useQuery({
    path: { question_id: questionIdNum },
  });

  if (isLoading) return <Message>Loading question...</Message>;
  if (error) return <Message $error>Error loading question</Message>;
  if (!data) return <Message $error>Question not found</Message>;

  // A draft in a quiz students may have can never be published, so it's view-only.
  const stuckDraft = data.quiz_locked && data.status !== 'published';

  return (
    <DetailPage>
      <ItemBand path={data.path} />
      <Breadcrumbs
        path={data.path}
        extra={[
          <Link
            key="quiz"
            to="/questions"
            search={{
              lesson: data.lesson_id,
              topic: data.topic_id ?? undefined,
            }}
          >
            {data.topic_id == null
              ? finalQuizTitle(
                  data.path.find((crumb) => crumb.kind === 'lesson')?.title,
                )
              : 'Topic quiz'}
          </Link>,
        ]}
        current={`Question #${data.id}`}
      />

      <Header>
        <TitleRow>
          <PageTitle>Question #{data.id}</PageTitle>
          <QuizBadge final={data.topic_id == null} />
          <ItemVersionMenu
            path={data.path}
            entity="question"
            lineageId={data.lineage_id}
          />
        </TitleRow>
        {!editing && !stuckDraft && (
          <Button className={buttonStyles.Button} onClick={startEditing}>
            Edit
          </Button>
        )}
      </Header>
      {stuckDraft && <StuckDraft data={data} />}

      {/* A stuck draft is delete-only, even when ?edit=true is in the URL. */}
      {editing && !stuckDraft ? (
        // key remounts the form per question, so its seeded state stays fresh.
        <QuestionEditor
          key={data.id}
          question={data}
          lessonId={data.lesson_id}
          topicId={data.topic_id ?? undefined}
          quizLocked={data.quiz_locked}
          onDone={stopEditing}
        />
      ) : (
        <QuestionView data={data} />
      )}
    </DetailPage>
  );
}

function StuckDraft({ data }: { data: QuestionAdmin }) {
  const navigate = useNavigate();
  const topic = data.topic_id ?? undefined;
  const deleteMutation = api.adminDeleteQuestion.useMutation(undefined, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: api.adminListQuestions.getQueryKey({
          path: { lesson_id: data.lesson_id },
        }),
      });
      navigate({ to: '/questions', search: { lesson: data.lesson_id, topic } });
    },
  });

  const handleDelete = () => {
    if (!window.confirm(deletePrompt('question', data))) return;
    deleteMutation.mutate({ path: { question_id: data.id } });
  };

  return (
    <StuckDraftBar>
      <LockedQuizNotice parent={topic ? 'topic' : 'lesson'} draft />
      <Button
        className={buttonStyles.Button}
        onClick={handleDelete}
        disabled={deleteMutation.isPending}
      >
        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
      </Button>
      {deleteMutation.isError && (
        <Message $error>
          {apiErrorDetail(deleteMutation.error) ??
            'Could not delete the question. Please try again.'}
        </Message>
      )}
    </StuckDraftBar>
  );
}

function HtmlSection({ title, html }: { title: string; html: string }) {
  if (!html) return null;
  return (
    <Section>
      <SectionTitle>{title}</SectionTitle>
      <Body>
        {/* biome-ignore lint/security/noDangerouslySetInnerHtml: admin-authored HTML, trusted in admin */}
        <RichContent dangerouslySetInnerHTML={{ __html: html }} />
      </Body>
    </Section>
  );
}

function QuestionView({ data }: { data: QuestionAdmin }) {
  const isCloze = data.type === 'fill_in_blank';
  const questionHtml = isCloze
    ? clozeHtml(
        data.cloze_replacer,
        (data.answers ?? []).map(acceptedSpellings),
        'answers',
      )
    : data.text;

  return (
    <>
      <Section>
        <SectionTitle>Overview</SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>Type</FieldLabel>
            <FieldValue>{QUESTION_TYPE_LABELS[data.type]}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Order</FieldLabel>
            <FieldValue>{data.order}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Points</FieldLabel>
            <FieldValue>{data.score}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Status</FieldLabel>
            <FieldValue>{data.status}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Lesson</FieldLabel>
            <FieldValue>
              <Link
                to="/lessons/$lessonId"
                params={{ lessonId: String(data.lesson_id) }}
              >
                #{data.lesson_id}
              </Link>
            </FieldValue>
          </Field>
          {data.topic_id != null && (
            <Field>
              <FieldLabel>Topic</FieldLabel>
              <FieldValue>
                <Link
                  to="/topics/$topicId"
                  params={{ topicId: String(data.topic_id) }}
                >
                  #{data.topic_id}
                </Link>
              </FieldValue>
            </Field>
          )}
        </FieldGrid>
      </Section>

      <Section>
        <SectionTitle>
          {isCloze ? 'Question (answers shown in the blanks)' : 'Question'}
        </SectionTitle>
        <Body>
          {questionHtml ? (
            <RichContent
              // biome-ignore lint/security/noDangerouslySetInnerHtml: admin-authored HTML, trusted in admin
              dangerouslySetInnerHTML={{ __html: questionHtml }}
            />
          ) : (
            <em>(no question text)</em>
          )}
        </Body>
      </Section>

      {!isCloze && data.answers && data.answers.length > 0 && (
        <Section>
          <SectionTitle>
            {data.disable_correct ? 'Options (no correct answer)' : 'Options'}
          </SectionTitle>
          {!data.disable_correct &&
            data.type !== 'essay' &&
            !data.answers.some((a) => a.is_correct) && (
              <NoCorrectWarning>
                No option is marked correct and "No correct answer" isn't
                ticked, so every answer to this question is marked wrong.
              </NoCorrectWarning>
            )}
          <AnswerList>
            {data.answers.map((a, i) => (
              <AnswerView key={i} answer={a} />
            ))}
          </AnswerList>
        </Section>
      )}

      <HtmlSection title="Hint" html={data.hint} />
      <HtmlSection title="Message when correct" html={data.correct_msg} />
      <HtmlSection title="Message when incorrect" html={data.incorrect_msg} />
      <HtmlSection
        title="Explanation for study helpers"
        html={data.explanation}
      />
    </>
  );
}

function AnswerView({ answer }: { answer: Answer }) {
  return (
    <AnswerRow $correct={answer.is_correct}>
      <AnswerMark>{answer.is_correct ? '✓' : ''}</AnswerMark>
      <RichContent
        // biome-ignore lint/security/noDangerouslySetInnerHtml: admin-authored HTML, trusted in admin
        dangerouslySetInnerHTML={{ __html: answer.text }}
      />
      {answer.is_correct && <small>correct</small>}
    </AnswerRow>
  );
}

const Header = styled.div`
  align-items: center;
  display: flex;
  gap: var(--component-margin);
  justify-content: space-between;
`;

const TitleRow = styled.div`
  align-items: center;
  display: flex;
  gap: 0.75rem;
`;

const StuckDraftBar = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: var(--component-margin);
`;

const Body = styled.div`
  padding: var(--inner-padding);
`;

const AnswerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: var(--inner-padding);
`;

const AnswerRow = styled.div<{ $correct: boolean }>`
  align-items: center;
  background: ${(p) =>
    p.$correct
      ? 'color-mix(in srgb, var(--success) 14%, transparent)'
      : 'transparent'};
  border-left: 3px solid
    ${(p) => (p.$correct ? 'var(--success)' : 'transparent')};
  border-radius: 4px;
  color: var(--grey-700);
  display: grid;
  gap: 0.5rem;
  grid-template-columns: 1.5rem 1fr auto;
  padding: 0.5rem 0.75rem;
`;

const NoCorrectWarning = styled.p`
  background: color-mix(in srgb, var(--orange) 12%, transparent);
  border-left: 3px solid var(--orange);
  border-radius: 0.25rem;
  color: var(--grey-800);
  font-size: 0.8125rem;
  line-height: 1.4;
  margin: 0 1rem 0.5rem;
  padding: 0.5rem 0.75rem;
`;

const AnswerMark = styled.span`
  color: var(--success);
  font-weight: 700;
  text-align: center;
`;

const Message = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;
